/**
 * Helpers for matching Firestore provider records to the authenticated Firebase user.
 */

type ProviderIdentityFields = {
  DocID?: string;
  id?: string;
};

/**
 * Resolve the canonical user id stored on a provider document.
 *
 * Firestore listeners attach the document id as `id`; legacy documents may only
 * expose `DocID` in the payload.
 *
 * @param provider - Provider record from Firestore, if loaded
 * @return Firebase uid for the provider, or empty string when unknown
 */
export function getProviderUserId(
  provider: ProviderIdentityFields | null | undefined,
): string {
  return provider?.DocID ?? provider?.id ?? "";
}

/**
 * Determine whether a provider record belongs to the signed-in user.
 *
 * @param provider - Provider record from Firestore, if loaded
 * @param userId - Authenticated Firebase uid
 * @return True when the provider record matches the user
 */
export function isProviderForUser(
  provider: ProviderIdentityFields | null | undefined,
  userId: string | undefined,
): boolean {
  if (!userId) {
    return false;
  }
  const providerUserId = getProviderUserId(provider);
  return Boolean(providerUserId) && providerUserId === userId;
}
